import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Toaster } from "./components/ui/toaster";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";

// Protected Route Component with backend session validation
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isLoading, sessionValidated } = useAuth();

  // Wait for session validation
  if (isLoading || !sessionValidated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Oturum doğrulanıyor...</p>
      </div>
    );
  }

  // Not logged in or session invalid - redirect to login
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check role access from backend-validated user
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Admin trying to access user dashboard -> redirect to admin
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    // Non-admin trying to access admin dashboard -> redirect to user dashboard
    // This is critical: non-admins CANNOT access /admin
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route - redirects to appropriate dashboard if logged in
const PublicRoute = ({ children }) => {
  const { user, isLoading, sessionValidated } = useAuth();

  if (isLoading || !sessionValidated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Yükleniyor...</p>
      </div>
    );
  }

  if (user) {
    // Redirect based on role from validated session
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register/:type"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* User Dashboard - ONLY for bireysel and toptanci */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['bireysel', 'toptanci']}>
            <UserDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin Dashboard - ONLY for admin role */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
